import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, FileDown, Mail, ShieldCheck, BookMarked } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const deDatum = (s) => (s ? `${s.slice(8, 10)}.${s.slice(5, 7)}.${s.slice(0, 4)}` : "?");
const TABS = { luecken: "Offene Unterrichtstage", unterlagen: "Fehlende Unterlagen", wochenstand: "Gesicherte Wochenstände" };

const Tab = ({ id, aktiv, onClick, anzahl }) => (
  <button onClick={onClick} data-testid={`kbl-tab-${id}`}
    className={`text-sm px-3 py-1.5 rounded transition-colors ${aktiv ? "bg-[#3D3835] text-white" : "border border-stone-300 hover:bg-stone-100"}`}>
    {TABS[id]}{anzahl != null && <span className="ml-1.5 opacity-70">({anzahl})</span>}
  </button>
);

const Leer = ({ text }) => <div className="text-sm text-stone-500 py-10 text-center" data-testid="kbl-leer">{text}</div>;

const LueckenTabelle = ({ daten }) => {
  const [kurs, setKurs] = useState("");
  const kurse = useMemo(() => [...new Set(daten.map((x) => x.kurs))], [daten]);
  const liste = daten.filter((x) => !kurs || x.kurs === kurs);
  if (!daten.length) return <Leer text="Alles erledigt – keine offenen Unterrichtstage." />;
  return (
    <div>
      <select value={kurs} onChange={(e) => setKurs(e.target.value)} className="mb-3 text-sm border border-stone-300 rounded px-2 py-1.5" data-testid="kbl-kurs-filter">
        <option value="">Alle Kurse ({daten.length})</option>
        {kurse.map((k) => <option key={k} value={k}>{k} ({daten.filter((x) => x.kurs === k).length})</option>)}
      </select>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="kbl-luecken-tabelle">
          <thead><tr className="text-left text-xs text-stone-500 border-b-2 border-stone-800">
            <th className="py-2 pr-3">Kurs</th><th className="py-2 pr-3">Tag</th><th className="py-2 pr-3">TN</th><th className="py-2 pr-3">Dozent/in</th><th className="py-2">Was fehlt</th>
          </tr></thead>
          <tbody>
            {liste.map((x) => (
              <tr key={`${x.kurs_id}-${x.datum}`} className="border-b border-stone-100" data-testid={`kbl-luecke-${x.kurs_id}-${x.datum}`}>
                <td className="py-2 pr-3 font-medium">{x.kurs}</td>
                <td className="py-2 pr-3 whitespace-nowrap">{x.datum_label}</td>
                <td className="py-2 pr-3">{x.teilnehmende}</td>
                <td className="py-2 pr-3">{x.dozent || "–"}</td>
                <td className="py-2"><div className="flex flex-wrap gap-1">{x.probleme.map((p) => <span key={p} className="text-[11px] px-2 py-0.5 rounded-full bg-[#FBEBE2] text-[#9A3B2E]">{p}</span>)}</div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const UnterlagenTabelle = ({ daten }) => {
  if (!daten.length) return <Leer text="Alle Unterlagen der laufenden AZAV-Kurse sind vollständig." />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="kbl-unterlagen-tabelle">
        <thead><tr className="text-left text-xs text-stone-500 border-b-2 border-stone-800">
          <th className="py-2 pr-3">Teilnehmer/in</th><th className="py-2 pr-3">Kurs</th><th className="py-2 pr-3">Start</th><th className="py-2">Fehlt</th>
        </tr></thead>
        <tbody>
          {daten.map((x) => (
            <tr key={x.teilnehmer_id} className="border-b border-stone-100" data-testid={`kbl-unterlage-${x.teilnehmer_id}`}>
              <td className="py-2 pr-3 font-medium">{x.name}</td>
              <td className="py-2 pr-3">{x.kurs.split(" (")[0]}</td>
              <td className="py-2 pr-3 whitespace-nowrap">{deDatum(x.start)}</td>
              <td className="py-2"><div className="flex flex-wrap gap-1">{x.luecken.map((l) => (
                <span key={l.kat} className="text-[11px] px-2 py-0.5 rounded-full bg-[#FBEBE2] text-[#9A3B2E]">{l.dokument}{l.art !== "Fehlt" ? ` – ${l.art}` : ""}</span>
              ))}</div></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const laden = async (url, name) => {
  try {
    const r = await axios.get(url, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data); a.download = name; a.click();
  } catch (e) { toast.error("Download fehlgeschlagen"); }
};

const WochenstandTabelle = ({ daten, darfSichern, onSichern }) => (
  <div>
    {darfSichern && (
      <button onClick={onSichern} data-testid="kbl-woche-sichern"
        className="mb-3 text-sm flex items-center gap-2 px-3 py-2 rounded bg-[#3D3835] text-white hover:bg-black transition-colors">
        <ShieldCheck size={14} /> Diese Woche jetzt sichern
      </button>
    )}
    <p className="text-xs text-stone-500 mb-3">Jeden Freitag um 18 Uhr wird der Wochenstand jedes Klassenbuchs automatisch als PDF mit Prüfsumme gesichert. Wird danach etwas geändert, entsteht automatisch eine neue Version. Alte Versionen bleiben erhalten.</p>
    {!daten.length ? <Leer text="Noch keine Wochenstände gesichert." /> : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="kbl-wochenstand-tabelle">
          <thead><tr className="text-left text-xs text-stone-500 border-b-2 border-stone-800">
            <th className="py-2 pr-3">Kurs</th><th className="py-2 pr-3">Woche</th><th className="py-2 pr-3">Version</th><th className="py-2 pr-3">Gesichert</th><th className="py-2 pr-3">Grund</th><th className="py-2 pr-3">Prüfsumme</th><th className="py-2"></th>
          </tr></thead>
          <tbody>
            {daten.map((w) => (
              <tr key={w.id} className="border-b border-stone-100" data-testid={`kbl-wochenstand-${w.id}`}>
                <td className="py-2 pr-3 font-medium">{w.kurs}</td>
                <td className="py-2 pr-3 whitespace-nowrap">KW {w.kw} · ab {deDatum(w.woche)}</td>
                <td className="py-2 pr-3">v{w.version}</td>
                <td className="py-2 pr-3 whitespace-nowrap">{new Date(w.erstellt_am).toLocaleString("de-DE", { timeZone: "Europe/Berlin" })}<div className="text-xs text-stone-500">{w.erstellt_von}</div></td>
                <td className="py-2 pr-3">{w.grund}</td>
                <td className="py-2 pr-3 font-mono text-[11px] text-stone-500" title={w.sha256}>{w.sha256.slice(0, 12)}…</td>
                <td className="py-2"><button onClick={() => laden(`${API}/klassenbuch-wochenstand/${w.id}/pdf`, `Klassenbuch_${w.kurs_id}_KW${w.kw}_v${w.version}.pdf`)}
                  data-testid={`kbl-wochenstand-pdf-${w.id}`} className="flex items-center gap-1 text-xs px-2 py-1 rounded border border-stone-300 hover:bg-stone-100"><FileDown size={12} /> PDF</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const MailVorschau = ({ onClose }) => {
  const [m, setM] = useState(null);
  useEffect(() => { axios.get(`${API}/klassenbuch-luecken/mail-vorschau`).then((r) => setM(r.data)).catch(() => toast.error("Vorschau fehlgeschlagen")); }, []);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="kbl-mail-dialog">
        <DialogHeader><DialogTitle>8-Uhr-Mail (Stand jetzt)</DialogTitle></DialogHeader>
        {!m ? <div className="text-sm text-stone-500">Lädt…</div> : (
          <div className="text-sm space-y-1">
            <div><b>An:</b> {m.an.join(", ")}</div>
            <div data-testid="kbl-mail-betreff"><b>Betreff:</b> {m.betreff}</div>
            <iframe title="Mail" srcDoc={m.html} sandbox="" className="w-full h-[60vh] border border-stone-200 rounded" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function KlassenbuchLuecken() {
  const { user } = useAuth();
  const [tab, setTab] = useState("luecken");
  const [luecken, setLuecken] = useState(null);
  const [unterlagen, setUnterlagen] = useState(null);
  const [wochen, setWochen] = useState(null);
  const [mail, setMail] = useState(false);
  const darfSichern = ["gf", "verwaltung"].includes(user?.role);

  const ladeWochen = () => axios.get(`${API}/klassenbuch-wochenstand`).then((r) => setWochen(r.data)).catch(() => setWochen([]));
  useEffect(() => {
    axios.get(`${API}/klassenbuch-luecken`).then((r) => setLuecken(r.data.luecken)).catch(() => setLuecken([]));
    axios.get(`${API}/klassenbuch-luecken/unterlagen`).then((r) => setUnterlagen(r.data)).catch(() => setUnterlagen([]));
    ladeWochen();
  }, []);

  const sichern = async () => {
    try {
      const r = await axios.post(`${API}/klassenbuch-wochenstand/sichern`, {});
      toast.success(`${r.data.gesichert} Klassenbuch-Woche(n) gesichert`);
      ladeWochen();
    } catch (e) { toast.error(e.response?.data?.detail || "Sichern fehlgeschlagen"); }
  };

  return (
    <div className="p-6 max-w-6xl" data-testid="klassenbuch-luecken-seite">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-[#3D3835] flex items-center gap-2"><BookMarked size={22} /> Lückenliste Klassenbuch</h1>
          <p className="text-sm text-stone-500 mt-1">Vergangene Unterrichtstage der BG-Kurse (PHK, LG1 &amp; LG2, Ambulant) ab {deDatum("2026-10-15")}, an denen etwas fehlt, und fehlende Unterlagen der laufenden AZAV-Kurse.</p>
        </div>
        <button onClick={() => setMail(true)} data-testid="kbl-mail-vorschau"
          className="text-sm flex items-center gap-2 px-3 py-2 rounded border border-stone-300 hover:bg-stone-100 transition-colors"><Mail size={14} /> 8-Uhr-Mail ansehen</button>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        <Tab id="luecken" aktiv={tab === "luecken"} onClick={() => setTab("luecken")} anzahl={luecken?.length} />
        <Tab id="unterlagen" aktiv={tab === "unterlagen"} onClick={() => setTab("unterlagen")} anzahl={unterlagen?.length} />
        <Tab id="wochenstand" aktiv={tab === "wochenstand"} onClick={() => setTab("wochenstand")} anzahl={wochen?.length} />
      </div>
      <div className="bg-white rounded-xl border border-stone-200 p-4">
        {tab === "luecken" && (luecken ? <LueckenTabelle daten={luecken} /> : <Leer text="Lädt…" />)}
        {tab === "unterlagen" && (unterlagen ? <UnterlagenTabelle daten={unterlagen} /> : <Leer text="Lädt…" />)}
        {tab === "wochenstand" && (wochen ? <WochenstandTabelle daten={wochen} darfSichern={darfSichern} onSichern={sichern} /> : <Leer text="Lädt…" />)}
      </div>
      <p className="text-xs text-stone-500 mt-3 flex items-center gap-1"><AlertTriangle size={12} /> Mo–Fr um 8 Uhr geht diese Liste per Mail an Julia, Annika und Linda.</p>
      {mail && <MailVorschau onClose={() => setMail(false)} />}
    </div>
  );
}

